<template lang="pug">
Modal(
    :open="props.showCreateModal"
    @close="handleClose"
)
    .bq-modal-wrap
        .bq-modal-close(@click="handleClose" :class="{disabled: promiseRunning}") &times;

        form.bq-modal-step(@submit.prevent="createProject")
            h2 Create a new project
            p.bq-modal-desc Give your project a name to get started. #[span.wordset Add a base prompt] to give the AI assistant context about your project.
            label.bq-field-label(for="createProjectName") Project name
            input#createProjectName.bq-modal-input(
                :placeholder="projectNamePlaceholder"
                maxlength="256"
                type="text"
                required
                :value="newServiceName"
                @input="newServiceName = $event.target.value"
                :disabled="promiseRunning"
                @keydown.enter.prevent="descriptionRef?.focus()"
            )
            label.bq-field-label(for="createProjectDescription") Base Prompt
            textarea#createProjectDescription.bq-modal-textarea(
                ref="descriptionRef"
                :placeholder="projectDescriptionPlaceholder"
                rows="3"
                :value="newServiceDescription"
                @input="newServiceDescription = $event.target.value"
                :disabled="promiseRunning"
            )
            button.btn(
                type="submit"
                :disabled="!newServiceName"
                :class="{ 'bq-btn--loading': promiseRunning }"
            )
                span.bq-loader(v-if="promiseRunning")
                template(v-else) + Create
            .error(v-if="error" style="margin-top: 0.75rem")
                span {{ error }}

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { ref, watch } from "vue";

import { type ServiceFromServer, createService } from "@/code/service";
import Modal from "@/components/modal.vue";
import { addServiceToList, pickProjectPlaceholders } from "./project-list";

const router = useRouter();
const route = useRoute();

const props = defineProps<{
    showCreateModal: boolean;
    firstServiceName?: string;
}>();
const emit = defineEmits(["close"]);

let promiseRunning = ref(false);
let newServiceName = ref("");
let newServiceDescription = ref("");
let descriptionRef = ref();
let error = ref("");

let projectNamePlaceholder = ref("");
let projectDescriptionPlaceholder = ref("");
const rollPlaceholders = () => {
    const p = pickProjectPlaceholders();
    projectNamePlaceholder.value = p.name;
    projectDescriptionPlaceholder.value = p.description;
};
rollPlaceholders();

const validateServiceName = (name: string): boolean => {
    let regex = /[<>`]/g;
    return !name.match(regex);
};

let createProject = async () => {
    if (promiseRunning.value) return;
    error.value = "";

    if (!validateServiceName(newServiceName.value)) {
        error.value = "Angle brackets and backticks are not allowed";
        return;
    }

    promiseRunning.value = true;

    let params: { name: string; description?: string } = { name: newServiceName.value };
    if (newServiceDescription.value.trim()) {
        params.description = newServiceDescription.value.trim();
    }

    let service = await createService(params).catch((err) => {
        error.value = err instanceof Error ? err.message : String(err);
        promiseRunning.value = false;
    });

    // console.log("Created service", service);
    addServiceToList([service as ServiceFromServer]).then(() => {
        handleClose();
        router.push(`/my-projects/${(service as ServiceFromServer).service}/agent`);
    });
};

const resetModalState = () => {
    promiseRunning.value = false;
    newServiceDescription.value = "";
    rollPlaceholders();

    if (!props.firstServiceName || !props.firstServiceName.trim()) {
        newServiceName.value = "";
    }
};

watch(
    () => props.showCreateModal,
    (newVisible) => {
        if (newVisible) {
            resetModalState();
        }
    }
);

const handleClose = () => {
    if (promiseRunning.value) return;
    resetModalState();
    emit("close");
};
</script>

<style scoped lang="less">
@import "@/assets/less/bq-tokens.less";
.btn {
    width: 100%;
}
// .bq-modal-wrap {
//     position: relative;
//     padding: 2.5rem;
//     font-family: @mono;
//     color: @ink;
//     // max-width: 28rem;
// }


.bq-modal-close {
    position: absolute;
    top: 1.25rem;
    right: 1.5rem;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    color: @muted;
    transition: color 0.15s;

    &:hover {
        color: @ink;
    }
}

.bq-modal-back {
    margin-bottom: 1.5rem;
    font-size: 0.85rem;
    color: @muted;
    cursor: pointer;
    display: inline-block;

    &:hover {
        color: @ink;
    }
}

.bq-modal-step {
    h2 {
        margin: 0 0 0.5rem;
        font-size: 1.3rem;
        font-weight: 600;
    }
}

.bq-modal-desc {
    margin: 0 0 1.5rem;
    font-size: 0.88rem;
    color: @muted;
    line-height: 1.6;
}

.bq-field-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: @ink;
    margin-bottom: 0.4rem;
    letter-spacing: 0.02em;
}

.bq-modal-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }
}

.bq-modal-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    margin-bottom: 0.75rem;
    border-radius: 0;
    transition: border-color 0.15s;
    resize: vertical;
    min-height: 4rem;
    word-wrap: break-word;
    overflow-x: hidden;
    white-space: pre-wrap;

    &::placeholder {
        color: @placeholder;
    }

    &:focus {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.bq-modal-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0.75rem 1.4rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font-family: @mono;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    box-shadow: 2px 2px rgba(0, 0, 0, 0.12);

    &:hover {
        background: @paper;
        color: @ink;
    }

    &:disabled {
        opacity: 0.3;
        cursor: default;
        pointer-events: none;
    }

    &::after {
        display: none;
    }
}
</style>
